import styles from 'css/src/components/app/chat-tab/components/ModelSettingsPopover.module.scss';

import React from 'react';
import classNames from 'classnames';

import type { ModelOption, ReasoningEffort } from 'js/src/agent/types';
import { getContextLimit, reasoningEffortOptionsForModel } from 'js/src/agent/types';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput from 'js/src/components/common/ui/TextInput';

interface ModelSettingsPopoverProps {
    selectedModel: ModelOption;
    modelOptions: ModelOption[];
    onSelectModel: (value: string) => void;
    reasoningEffort?: ReasoningEffort;
    onSelectReasoningEffort?: (value: ReasoningEffort) => void;
    /** Developer override for the Responses API compaction threshold (tokens);
     * undefined means the model default is in effect. */
    compactionThreshold?: number;
    /** The threshold used when no override is set, shown as the field's placeholder. */
    defaultCompactionThreshold?: number;
    /** Called with the new override (or undefined to clear it). Required for the field to show. */
    onCompactionThresholdChange?: (threshold: number | undefined) => void;
}

/** Blank clears the override; anything else must be a positive whole number. */
function parseThresholdDraft(draft: string): number | undefined | null {
    const trimmed = draft.trim();
    if (trimmed === '') {
        return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

const LABEL_CLASS = 'font-l5 font-l5--subtext color-grey-600 d-block mb-l5-4';

const ModelSettingsPopover: React.FC<ModelSettingsPopoverProps> = ({
    selectedModel,
    modelOptions,
    onSelectModel,
    reasoningEffort,
    onSelectReasoningEffort,
    compactionThreshold,
    defaultCompactionThreshold,
    onCompactionThresholdChange,
}) => {
    const [open, setOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const [thresholdDraft, setThresholdDraft] = React.useState(
        compactionThreshold === undefined ? '' : String(compactionThreshold),
    );
    React.useEffect(() => {
        setThresholdDraft(compactionThreshold === undefined ? '' : String(compactionThreshold));
    }, [compactionThreshold]);

    const parsedThreshold = parseThresholdDraft(thresholdDraft);
    const isThresholdInvalid = parsedThreshold === null;
    const commitThreshold = () => {
        if (!isThresholdInvalid && parsedThreshold !== compactionThreshold) {
            onCompactionThresholdChange?.(parsedThreshold);
        }
    };
    const commitThresholdRef = React.useRef(commitThreshold);
    commitThresholdRef.current = commitThreshold;

    React.useEffect(() => {
        if (!open) {
            return undefined;
        }
        const close = () => {
            commitThresholdRef.current();
            setOpen(false);
        };
        const handlePointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                close();
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const reasoningOptions = reasoningEffortOptionsForModel(selectedModel.value);
    const contextLimit = getContextLimit(selectedModel.value);

    return (
        <div ref={containerRef} className="position-relative">
            <button
                type="button"
                aria-label="Model settings"
                aria-expanded={open}
                className="btn btn-link p-l5-0 text-decoration-none color-grey-500"
                style={{ fontSize: 11 }}
                onClick={() => setOpen((prev) => !prev)}
            >
                {selectedModel.label}
            </button>
            {open && (
                <div
                    className={classNames(
                        styles['panel'],
                        'card position-absolute end-0 bottom-100 mb-l5-4 p-l5-12 text-start font-l5 font-l5--subtext',
                    )}
                    role="dialog"
                    aria-label="Model settings"
                >
                    <label className={LABEL_CLASS} htmlFor="model-settings-model">
                        Model
                    </label>
                    <select
                        id="model-settings-model"
                        className="form-select form-select-sm mb-l5-8"
                        value={selectedModel.value}
                        onChange={(e) => onSelectModel(e.target.value)}
                    >
                        {modelOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {onSelectReasoningEffort && reasoningOptions.length > 0 && (
                        <>
                            <label className={LABEL_CLASS} htmlFor="model-settings-reasoning">
                                Reasoning effort
                            </label>
                            <select
                                id="model-settings-reasoning"
                                className="form-select form-select-sm mb-l5-8"
                                value={reasoningEffort ?? reasoningOptions[0].value}
                                onChange={(e) => {
                                    const picked = reasoningOptions.find(
                                        (option) => option.value === e.target.value,
                                    );
                                    if (picked) {
                                        onSelectReasoningEffort(picked.value);
                                    }
                                }}
                            >
                                {reasoningOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}

                    {onCompactionThresholdChange && (
                        <>
                            <label className={LABEL_CLASS} htmlFor="model-settings-compaction">
                                Compaction threshold (tokens)
                            </label>
                            <TextInput
                                id="model-settings-compaction"
                                type="number"
                                text={thresholdDraft}
                                onChange={(e: TypeInputChangeEvent) =>
                                    setThresholdDraft(e.target.value)
                                }
                                onBlur={commitThreshold}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        commitThreshold();
                                    }
                                }}
                                placeholder={
                                    defaultCompactionThreshold === undefined
                                        ? 'model default'
                                        : `${defaultCompactionThreshold.toLocaleString()} (default)`
                                }
                            />
                            {isThresholdInvalid && (
                                <div className="color-error-500 mt-l5-4">
                                    Enter a positive whole number, or leave blank for the default.
                                </div>
                            )}
                        </>
                    )}

                    <div className="color-grey-500 mt-l5-8">
                        Context limit {contextLimit.toLocaleString()} tokens
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModelSettingsPopover;
